import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getSalesOrderLines } from '../services/api';

interface Props {
  // The customer's editable sales orders (from getEditableOrders).
  orders: any[];
  // Currently-selected SO id, or null for the "New order…" option.
  value: number | null;
  // Fired when the rep picks an order (or clears back to New order…).
  onChange: (id: number | null) => void;
  className?: string;
}

// Cached per-SO line fetch: undefined = never hovered; else its load state.
type LineState = { status: 'loading' | 'ok' | 'error'; lines?: any[] };

// A custom replacement for the native <select> of editable orders. Native
// <option>s can't host a hover card, so this renders the list in a PORTAL (fixed
// positioning, never clipped by the modal's overflow-hidden — same idiom as
// CustomerConfirmSelect) and, when a row is hovered, shows a small popover
// previewing that order's line items / quantities (lazily fetched + cached).
const EditableOrderSelect: React.FC<Props> = ({ orders, value, onChange, className }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [hoverId, setHoverId] = useState<number | null>(null);
  const [hoverRect, setHoverRect] = useState<{ top: number; menuLeft: number; menuRight: number } | null>(null);
  const [cache, setCache] = useState<Record<number, LineState>>({});
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const label = (o: any) =>
    `${o.tranid || 'SO ' + o.id}${o.trandate ? ' — ' + o.trandate : ''}${o.foreigntotal != null ? ' ($' + o.foreigntotal + ')' : ''}`;

  const selected = orders.find((o) => Number(o.id) === Number(value));

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setMenuPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const inside = (t: EventTarget | null) =>
      t instanceof Node && (!!btnRef.current?.contains(t) || !!menuRef.current?.contains(t));
    const onScroll = (e: Event) => { if (!inside(e.target)) close(); else place(); };
    const onDocClick = (e: MouseEvent) => { if (!inside(e.target)) close(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => { setOpen(false); setHoverId(null); setHoverRect(null); };

  // Lazily fetch (and cache) an order's lines the first time it's hovered.
  const ensureLines = (soId: number) => {
    if (cache[soId]) return;
    setCache((c) => ({ ...c, [soId]: { status: 'loading' } }));
    getSalesOrderLines(soId)
      .then((lines) => setCache((c) => ({ ...c, [soId]: { status: 'ok', lines: lines || [] } })))
      .catch(() => setCache((c) => ({ ...c, [soId]: { status: 'error' } })));
  };

  const onRowHover = (o: any, e: React.MouseEvent<HTMLButtonElement>) => {
    const id = Number(o.id);
    setHoverId(id);
    ensureLines(id);
    const rowRect = e.currentTarget.getBoundingClientRect();
    const menuEl = menuRef.current?.getBoundingClientRect();
    setHoverRect({
      top: rowRect.top,
      menuLeft: menuEl ? menuEl.left : rowRect.left,
      menuRight: menuEl ? menuEl.right : rowRect.right,
    });
  };

  const pick = (id: number | null) => { onChange(id); close(); };

  // Popover geometry: sit to the RIGHT of the menu, unless that overflows the
  // viewport, in which case flip to the LEFT.
  const POP_W = 260;
  const popStyle: React.CSSProperties | null = hoverRect
    ? (() => {
        const spaceRight = window.innerWidth - hoverRect.menuRight - 12;
        const left = spaceRight >= POP_W
          ? hoverRect.menuRight + 8
          : Math.max(8, hoverRect.menuLeft - POP_W - 8);
        return { position: 'fixed', top: Math.max(8, hoverRect.top), left, width: POP_W, zIndex: 81 };
      })()
    : null;

  const hovered = hoverId != null ? cache[hoverId] : undefined;

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        className={className}
        title="Pick an existing order to edit (hover a row to preview its items)"
      >
        <span className="truncate">{selected ? label(selected) : 'New order…'}</span>
        <svg className="w-4 h-4 ml-1 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && menuPos && createPortal(
        <>
          <div
            ref={menuRef}
            style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, minWidth: menuPos.width, zIndex: 80 }}
            className="max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl text-left py-1"
          >
            <button
              type="button"
              onClick={() => pick(null)}
              onMouseEnter={() => { setHoverId(null); setHoverRect(null); }}
              className={'block w-full text-left px-3 py-1.5 text-sm hover:bg-brand-50 ' + (value == null ? 'text-brand-700 font-medium' : 'text-gray-700')}
            >
              New order…
            </button>
            {orders.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => pick(Number(o.id))}
                onMouseEnter={(e) => onRowHover(o, e)}
                className={'block w-full text-left px-3 py-1.5 text-sm hover:bg-brand-50 whitespace-nowrap ' + (Number(o.id) === Number(value) ? 'text-brand-700 font-medium' : 'text-gray-700')}
              >
                {label(o)}
              </button>
            ))}
          </div>

          {hoverId != null && popStyle && (
            <div
              style={popStyle}
              className="bg-white border border-gray-200 rounded-lg shadow-xl p-2 text-left"
            >
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 px-1 pb-1">
                Order items
              </div>
              {!hovered || hovered.status === 'loading' ? (
                <div className="px-1 py-1 text-xs text-gray-400">Loading…</div>
              ) : hovered.status === 'error' ? (
                <div className="px-1 py-1 text-xs text-red-500">Couldn’t load items</div>
              ) : (hovered.lines || []).length === 0 ? (
                <div className="px-1 py-1 text-xs text-gray-400">No line items</div>
              ) : (
                <ul className="space-y-0.5 max-h-60 overflow-y-auto">
                  {(hovered.lines || []).map((ln: any, i: number) => (
                    <li key={ln.line ?? i} className="flex items-baseline gap-2 text-xs text-gray-700 px-1">
                      <span className="font-medium tabular-nums flex-shrink-0">{ln.quantity ?? '—'}</span>
                      <span className="text-gray-400 flex-shrink-0">×</span>
                      <span className="truncate" title={ln.item_name || ''}>{ln.item_name || `Item ${ln.item_id ?? ''}`}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>,
        document.body
      )}
    </div>
  );
};

export default EditableOrderSelect;
